/**
 * `useChipState` — React hook that derives the per-tile `ChipState` from
 * the live proxy + device snapshot for a given `deviceId`. Wraps the
 * pure `deriveChipState` (W3) with the data-fetch side it depends on:
 * `useRoom` for the device record, `locateProxy` for the agent's
 * StatusDevice snapshot.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global): the chip
 * derivation logic was previously inlined inside both `<Item>` (sidebar)
 * and `<Annotation>` (main-grid overlay). Extracting it gives both
 * surfaces a single source of truth for the chip verdict.
 *
 * Per `think-in-systems` (ai.knowledge/global): keep the pure helper
 * (`deriveChipState`) framework-agnostic and unit-testable; this hook
 * is the React-shaped adapter the consumers compose with.
 */
import { locateProxy } from '#~/agent/utils.js';
import { toStatus } from '#~/device/index.js';
import { useRoom } from '#~/room/hooks.js';
// Transitory-placeholder slice (W2, spec §2.1). When `room.devices[id]`
// is empty the id may be a placeholder; fall back to the slice so the
// chip resolves to "Authenticate" (spec §3.2/§3.3).
import { useTransitoryDevice } from '#~/device/transitory.js';
import type { Agent } from '@studio/models/agent';
import type { Device, Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { dequal } from 'dequal/lite';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { shallow } from 'zustand/shallow';
import { depot } from '#~/depot/depot.js';
import { useDepot } from '#~/depot/hooks.js';
import {
  getRegistryRequestScope,
  scopedTemplateKey,
  subscribeRegistryOrganizationScope
} from '#~/device/registry/registry-config-cache.js';
import { deriveSetupAttention } from '#~/device/setup-attention.js';
import {
  NO_CONNECTION_HOLD,
  isAspectResolved,
  isVideoAspectResolved,
  noteConnectionObservation
} from './connection-failure-hold.js';
import type { MultiAddSkipReason } from '#~/notifications/types.js';
import type { ChipState } from './chip-state.js';
import { deriveChipState, deriveMissingBuckets } from './chip-state.js';

/**
 * Derive the chip state for the device with the given `deviceId`.
 *
 * Returns `null` when the device is missing from the store, healthy, or
 * still in a transient state (UNKNOWN / VALIDATING / CONNECTING).
 * Otherwise returns the same `ChipState` shape `<Item>` already consumes.
 */
export const useChipState = (deviceId: Device['id']): ChipState | null => {
  const device = useRoom((µ) => µ.devices[deviceId], dequal);
  // Placeholder fallback (spec §3.2): a transitory id never lands in
  // `room.devices` (agent authority, §1.2), so a missing real device may
  // still be a placeholder. `transitoryKind` is the discriminator the
  // pure `deriveChipState` reads to return "Authenticate" (spec §3.3).
  const placeholder = useTransitoryDevice(deviceId);
  const transitoryKind = placeholder?.transitoryKind;

  // Read the status off the studio Device. Memoize so the chipState dep
  // array is stable across renders that don't change the device.
  const status = useMemo(
    // toStatus accepts the full Device type; the union shape is fine.
    // @ts-ignore — same cast used in Annotation's previous inline read
    () => (device ? toStatus(device) : undefined),
    [device]
  );

  // Reach into the agent state-sync tree for the raw StatusDevice
  // snapshot. `locateProxy` is sync and side-effect-free at call time.
  const proxy = locateProxy(deviceId);
  const proxyStatus = proxy?.state.status?.device[deviceId];
  const agentStatus = status?.device?.agentStatus;
  // `modelId` drives the "Select model" branch (Phase 1 manual QA).
  // Undefined when the discovery never resolved to a registry model
  // (no fingerprint match, unrecognized NDI source, ONVIF without
  // driver, etc.).
  const modelId = (device as { modelId?: T_Maybe<Model['id']> } | undefined)?.modelId;
  // Embedded (Iris-enabled) devices: suppress video/control/model chips.
  const isEmbedded = proxy?.embedded ?? false;

  // Held verdict via the depot: keeps this hook and the notification hook in sync, and is this hook's only reactive link to the proxy tree.
  const heldConnectionBuckets = useDepot(
    (µ) =>
      (µ.connectionFailureHoldByDevice[deviceId] as MultiAddSkipReason[] | undefined) ??
      NO_CONNECTION_HOLD,
    shallow
  );

  const orgScope = useSyncExternalStore(
    subscribeRegistryOrganizationScope,
    getRegistryRequestScope
  );

  // Both camera surfaces subscribe to the same explicit auth/driver evidence.
  const setupAttention = useDepot((µ) =>
    deriveSetupAttention(
      device ?? placeholder,
      modelId ? µ.registry.templates[scopedTemplateKey(orgScope, modelId)]?.model : undefined,
      proxyStatus
    )
  );

  const chipState = useMemo(
    // Cast: chip-state.ts accepts a mutable `StatusDevice`; the broker
    // exposes a `ReadonlyDeep` snapshot. The helper only reads — safe.
    // `transitoryKind` (when set) makes `deriveChipState` short-circuit to
    // the "Authenticate" placeholder branch (spec §3.3).
    () =>
      deriveChipState(proxyStatus as unknown as Parameters<typeof deriveChipState>[0], {
        agentStatus,
        modelId,
        transitoryKind,
        isEmbedded,
        heldConnectionBuckets,
        setupAttention
      }),
    [
      proxyStatus,
      agentStatus,
      modelId,
      transitoryKind,
      isEmbedded,
      heldConnectionBuckets,
      setupAttention
    ]
  );

  // Fold what this render observed into the hold. Cheap and idempotent: the
  // mutation leaves the draft untouched when nothing changed, so a steady
  // device never churns depot subscribers.
  useConnectionHoldWriter(deviceId, proxyStatus, agentStatus, isEmbedded);

  return chipState;
};

// Fold each observed status into the per-device hold. `present` stops a hold outliving the device (an offline bridge
// would leave it stuck and suppress the never-auto-dismiss BRIDGE_OFFLINE badge). Buckets are derived WITHOUT the hold so it can't feed itself.
const useConnectionHoldWriter = (
  deviceId: Device['id'],
  proxyStatus: unknown,
  agentStatus: T_Maybe<Agent['status']>,
  isEmbedded: boolean
): void => {
  const status = proxyStatus as
    | {
        video?: { connectionStatus?: { status?: number } };
        movement?: { connectionStatus?: { status?: number } };
      }
    | undefined;
  const videoStatus = status?.video?.connectionStatus?.status;
  const controlStatus = status?.movement?.connectionStatus?.status;
  const absent = status === undefined;

  useEffect(() => {
    // Positive test: a removed device keeps a status snapshot but loses its record (agentStatus), so require 'online', not !== 'offline'.
    const present = !absent && agentStatus === 'online';

    // `modelId` is intentionally omitted: this writer only feeds the two
    // CONNECTION buckets, and `noteConnectionObservation` filters everything
    // else out via `isHeldBucket`, so a 'missing-model' bucket derived here is
    // discarded rather than held.
    const missing = deriveMissingBuckets(
      proxyStatus as unknown as Parameters<typeof deriveMissingBuckets>[0],
      // `isEmbedded` is threaded so the writer and the reader agree: the
      // cascade suppresses video/control chips for embedded devices, so
      // without it the hold would accumulate entries nothing can ever show.
      { agentStatus, isEmbedded }
    );

    depot.getState().produce((draft) => {
      noteConnectionObservation(draft, deviceId, {
        missing,
        // Video releases ONLY on a genuine CONNECTED(3): the CONNECTING leg's
        // momentary UNKNOWN(0) is jitter, and the loose `isAspectResolved`
        // would treat it as resolved and drop the hold mid-oscillation.
        videoResolved: isVideoAspectResolved(videoStatus),
        // Control keeps the loose variant: a driver retarget legitimately
        // removes the aspect (UNKNOWN/undefined), which must release the hold.
        controlResolved: isAspectResolved(controlStatus),
        present
      });
    });
  }, [deviceId, proxyStatus, agentStatus, isEmbedded, videoStatus, controlStatus, absent]);
};
